import React from "react";
import BeatLoader from "react-spinners/BeatLoader";

// Resource picker (plan §5.2). In service-first order it lists the resources that
// perform the chosen service, gaining a "First Available" pseudo-option when
// `showFirstAvailable` is on. In resource-first order it leads and lists everything.
//
// A "resource" is not necessarily a person — it can be a room, a lane, a chair. All
// copy here is driven by the business's own word for them (`label`), falling back to
// noun-free wording rather than guessing (plan §16.1).

export const ANY_RESOURCE = "any";

type Resource = {
  id: string;
  name: string;
  displayName?: string;
  bio?: string;
  imageUrl?: string;
};

/** "a stylist" / "an artist" / "an option" — good enough for the labels owners type. */
const withArticle = (noun: string) => `${/^[aeiou]/i.test(noun) ? "an" : "a"} ${noun}`;

const SelectResourceStep = ({
  resources,
  onSelect,
  buttonStyle,
  loading,
  showFirstAvailable = false,
  showPhotos = true,
  label = { singular: "", plural: "" },
  title = ""
}) => {
  const singular = (label?.singular || "").trim();
  const plural = (label?.plural || "").trim();

  const heading = title || `Select ${withArticle(singular || "option")}`;
  const emptyMessage = plural
    ? `No ${plural.toLowerCase()} are available for this service right now.`
    : "Nothing is available for this service right now.";
  const anyDescription = plural
    ? `Book the soonest opening with any of our ${plural.toLowerCase()}`
    : "Book the soonest available opening";

  if (loading) {
    return (
      <div className="appointments__loading">
        <BeatLoader margin={2.5} size={15} color={buttonStyle?.backgroundColor} />
      </div>
    );
  }

  if (!resources?.length) {
    return (
      <div className="appointments__step">
        <div className="self-reservations__title">{heading}</div>
        <div className="appointments__empty">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="appointments__step">
      <div className="self-reservations__title">{heading}</div>
      <div className="appointments__list">
        {showFirstAvailable && (
          <button
            type="button"
            className="appointments__option appointments__option--highlight"
            onClick={() => onSelect({ id: ANY_RESOURCE, name: "First Available" })}
          >
            <span className="appointments__option-main">
              <span className="appointments__option-name">First Available</span>
              <span className="appointments__option-description">{anyDescription}</span>
            </span>
          </button>
        )}
        {resources.map((resource: Resource) => (
          <button key={resource.id} type="button" className="appointments__option" onClick={() => onSelect(resource)}>
            {showPhotos && !!resource.imageUrl && (
              <img
                className="appointments__option-avatar"
                src={resource.imageUrl}
                alt={resource.displayName || resource.name}
              />
            )}
            <span className="appointments__option-main">
              <span className="appointments__option-name">{resource.displayName || resource.name}</span>
              {!!resource.bio && <span className="appointments__option-description">{resource.bio}</span>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SelectResourceStep;
